import { Injectable } from '@angular/core';
import { LL, BOOT_LOCALE } from 'i18n/i18n.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { LoadingController } from 'providers/loading-controller';
import { CloudFunctionsService } from 'services/cloud-functions.service';
import { LoggingService } from 'services/logging.service';
import { TelemetryService } from 'services/telemetry.service';
import { UrlQueryService } from 'services/url-query-service';
import { getSharedUrlOrigin } from 'util/backend-origin';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import { TranslatedString } from 'models/translated-string';
import { LinkResendOutcome } from 'services/deep-link-signin';
import {
  ACCOUNT_HINT_PARAM,
  AccountHintHolder,
  AccountHintRejection,
  AccountHintParse,
  PostSignInOffer,
  compareWithHint,
  decidePostSignInOffer,
  parseAccountHint,
  shouldAskOnSignedInArrival,
} from 'services/account-hint';

/** Which moment the hint was checked at. Sent to telemetry as `trigger`. */
export enum AccountHintTrigger {
  /** Right after a sign-in on the login page. */
  AfterSignIn = 'after_sign_in',
  /** The app finished loading with somebody already signed in. */
  AlreadySignedIn = 'already_signed_in',
  /** A link was tapped while the app was already open (native only). */
  DeepLink = 'deep_link',
}

/** What the boot knows about the signed-in person, asked at the moment the hint is checked. */
export interface SignedInArrivalFacts {
  /** The address of the account they are in, or null when it has none or nobody is signed in. */
  signedInEmail: string | null;
  /** False when nobody is signed in: the hint is then kept for the login page. */
  signedIn: boolean;
  /** See `SignedInArrivalContext.otherLinkFlowPending` in account-hint.ts. */
  otherLinkFlowPending: boolean;
  /** See `SignedInArrivalContext.actingForSomeoneElse` in account-hint.ts. */
  actingForSomeoneElse: boolean;
}

/** What the login page knows about a sign-in that has just succeeded. */
export interface PostSignInFacts {
  signedInEmail: string | null;
  userPressedSignIn: boolean;
  accountWasJustCreated: boolean;
}

/**
 * The Angular half of the account hint on onboarding links (#5895). The rules are in
 * `account-hint.ts`, which has no Angular in it so the unit tests can load it; this class
 * supplies the dialog, the email send, the telemetry, and the two moments it is asked.
 *
 * Nothing here is ever given the power to sign anybody in. The one action it takes is
 * asking the server to email a sign-in link TO THE HINTED ADDRESS — the same thing the
 * login page's "Email me a sign-in link" button does for any address anybody types. What
 * then happens when that link is tapped is the existing account-switch question in
 * `deep-link-signin.ts`. The address itself is never logged and never sent to telemetry.
 *
 * Design and trade-offs: design-docs/purchase-deep-linking.md, "The account hint on
 * onboarding links (#5895)".
 */
@Injectable({ providedIn: 'root' })
export class AccountHintService {
  private readonly holder = new AccountHintHolder();
  /** Set once a signed-in app has finished loading, so a dialog has somewhere to appear. */
  private arrivalFacts: (() => SignedInArrivalFacts) | null = null;

  constructor(
    private urlQuery: UrlQueryService,
    private alertCtrl: AlertController,
    private loadingCtrl: LoadingController,
    private cloudFunctions: CloudFunctionsService,
    private logging: LoggingService,
    private telemetry: TelemetryService,
  ) {}

  /**
   * Reads the hint out of the address the app was opened at, and removes it from the
   * address bar straight away. Called once, at the very start of boot.
   *
   * The parameter is removed even when it was refused: a refused value may still be
   * somebody's address, and while it sits in the address bar Sentry and Mixpanel record
   * it with every event. The redaction rule in `breadcrumb-redaction.ts` covers the few
   * moments before this runs.
   */
  captureFromBootUrl() {
    const raw = this.urlQuery.getUrlParam(ACCOUNT_HINT_PARAM);
    if (raw == null) return;
    this.remember(parseAccountHint(raw));
    this.urlQuery.clearUrlSearchKeys([ACCOUNT_HINT_PARAM]);
  }

  /**
   * Takes a hint that arrived with a deep link (native). When a signed-in app is already
   * showing, it is checked straight away; otherwise it waits for the login page or for
   * the boot to finish.
   */
  noteFromDeepLink(parse: AccountHintParse) {
    this.remember(parse);
    if (parse.kind === 'valid' && this.arrivalFacts) {
      void this.offerOnArrivalAsync(AccountHintTrigger.DeepLink);
    }
  }

  /** The hint without using it up, for pre-filling the login form and preselecting a Google account. */
  peek(): string | null {
    return this.holder.peek();
  }

  /**
   * Called by the boot each time a signed-in app has finished loading. From then on a
   * dialog has somewhere to appear, so the hint is checked now, and a later deep link is
   * checked the moment it arrives.
   *
   * Why not earlier: the dialog renders into `<popover-host>`, which lives inside
   * app.html's `@if (!appLoading)` block. Before the loading screen comes down there is no
   * host to show it in.
   */
  onSignedInAppReady(facts: () => SignedInArrivalFacts) {
    this.arrivalFacts = facts;
    void this.offerOnArrivalAsync(AccountHintTrigger.AlreadySignedIn);
  }

  /**
   * Decides and asks the one question that follows a sign-in on the login page. Answers
   * whether a dialog ran, which is what tells the login page not to raise the boot splash
   * (see `LoginPage.finishSignInAsync`).
   *
   * `offerRecoveryAsync` is the #4936 question, owned by the login page and passed in so
   * the choice between the two lives in one place (`decidePostSignInOffer`).
   */
  async offerAfterSignInAsync(facts: PostSignInFacts, offerRecoveryAsync: () => Promise<boolean>): Promise<boolean> {
    // taken synchronously, before any await, so the boot's own check cannot also act on it
    const accountHint = this.holder.consume();
    const offer = decidePostSignInOffer({ ...facts, accountHint });
    if (accountHint) this.trackChecked(AccountHintTrigger.AfterSignIn, accountHint, facts.signedInEmail);

    switch (offer) {
      case PostSignInOffer.AccountHintMismatch:
        await this.askAboutHintedAccountSafelyAsync(AccountHintTrigger.AfterSignIn, accountHint, facts.signedInEmail);
        return true;
      case PostSignInOffer.ExistingAccountRecovery:
        return await offerRecoveryAsync();
      case PostSignInOffer.None:
        return false;
    }
  }

  /** Keeps a usable hint; reports a refused one without its value. */
  private remember(parse: AccountHintParse) {
    if (parse.kind === 'valid') {
      this.holder.remember(parse.email);
    } else if (parse.kind === 'malformed' && parse.reason === AccountHintRejection.UnfilledTag) {
      // the email template sent its placeholder instead of the address: a person has to
      // fix the template, and until then the hint does nothing for anyone
      this.logging.trackSilentProblem('AccountHintTagUnfilled', { reason: parse.reason });
    } else if (parse.kind === 'malformed') {
      // an address we refuse to show; expected now and then, and nothing to fix. The
      // value itself may be somebody's address and is never sent
      this.logging.trackSilentInfo('AccountHintMalformed', { reason: parse.reason });
    }
  }

  /** Checks the hint for somebody already signed in, once a dialog has somewhere to appear. */
  private async offerOnArrivalAsync(trigger: AccountHintTrigger) {
    const facts = this.arrivalFacts?.();
    // if nobody is signed in, keep the hint: the login page uses it after they sign in
    if (!facts?.signedIn) return;
    const accountHint = this.holder.consume();
    if (!accountHint) return;

    this.trackChecked(trigger, accountHint, facts.signedInEmail);
    if (!shouldAskOnSignedInArrival({ ...facts, accountHint })) return;
    await this.askAboutHintedAccountSafelyAsync(trigger, accountHint, facts.signedInEmail);
  }

  /**
   * The dialog, with every failure inside it reported rather than thrown.
   *
   * This is a safety net, never a gate: nothing about the sign-in the person already has
   * depends on it, so a failure here must not surface as a failed sign-in.
   */
  private async askAboutHintedAccountSafelyAsync(trigger: AccountHintTrigger, accountHint: string, signedInEmail: string) {
    try {
      await this.askAboutHintedAccountAsync(trigger, accountHint, signedInEmail);
    } catch (error) {
      this.logging.trackSilentProblem('AccountHint.offerFailed', {
        trigger,
        code: (error as any)?.code,
        message: describeError(error),
      });
      console.error('AccountHint: the account-hint offer failed:', describeErrorForLog(error));
    }
  }

  /** Asks whether to email the hinted address a sign-in link, and sends it until it goes out or they stop. */
  private async askAboutHintedAccountAsync(trigger: AccountHintTrigger, accountHint: string, signedInEmail: string) {
    const wantsLink = await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.auth.accountHint.title(),
      // `message`, never `messageMarkdown`: the hint comes from a link anybody can write,
      // and plain text is shown escaped
      message: LL.auth.accountHint.message({ hintEmail: accountHint, currentEmail: signedInEmail }),
      enableBackdropDismiss: true,
      buttons: [
        { text: LL.auth.accountHint.stayButton(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.auth.accountHint.sendButton(), role: AlertButtonRole.Primary, result: true },
      ],
      // tapping outside the dialog means staying where they are
      implicitCancelResult: false,
    });
    // only an explicit Send counts: the alert controller can hand back ANOTHER open
    // alert's answer instead of showing this one, and that must never send a link
    if (wantsLink !== true) {
      this.telemetry.trackEvent('account_hint_answered', { trigger, hint_answer: 'stay', link_send_result: 'none' });
      return;
    }

    // while the send fails and they choose to try again, send again. It cannot spin:
    // every failed pass waits for the person, and Cancel ends it.
    let outcome: LinkResendOutcome;
    do {
      outcome = await this.sendLinkAsync(accountHint);
      this.telemetry.trackEvent('account_hint_answered', { trigger, hint_answer: 'send_link', link_send_result: outcome });
    } while (outcome === LinkResendOutcome.SendFailed && (await this.askToTryAgainAsync()) === true);
    await this.reportSendOutcomeAsync(outcome, accountHint);
  }

  /** Tells the person what became of the link. A failed send they chose not to retry needs no more words. */
  private async reportSendOutcomeAsync(outcome: LinkResendOutcome, accountHint: string) {
    switch (outcome) {
      case LinkResendOutcome.Sent:
        await this.showMessageAsync(LL.auth.newAccountMade.linkSent({ email: accountHint }));
        return;
      case LinkResendOutcome.AccountAmbiguous:
        // the server refuses to send when several accounts hold the address (#4505),
        // so there is nothing to retry — they need a person
        await this.showMessageAsync(LL.auth.newAccountMade.severalAccounts());
        return;
      case LinkResendOutcome.SendFailed:
        return;
    }
  }

  /** Emails a sign-in link to the hinted address. Never to anybody else. */
  private async sendLinkAsync(accountHint: string): Promise<LinkResendOutcome> {
    const loading = this.loadingCtrl.create({ content: LL.auth.accountHint.sending() });
    // awaited, so the spinner is up before the network call and dismiss cannot overtake it
    await loading.present();
    try {
      const result = await this.cloudFunctions.signInByLink({
        email: accountHint,
        newUser: false,
        baseUrl: getSharedUrlOrigin(),
        // the campaign's own tracking travels on into the emailed link, as it does
        // for the login page's own "Email me a sign-in link" button
        trackingParams: this.urlQuery.getTrackingDataAsUrlParams(),
        uiLanguage: BOOT_LOCALE,
      });
      return result?.accountAmbiguous ? LinkResendOutcome.AccountAmbiguous : LinkResendOutcome.Sent;
    } catch (error) {
      this.logging.trackSilentProblem('AccountHint.linkSendFailed', {
        code: (error as any)?.code,
        message: describeError(error),
      });
      console.error('AccountHint: could not send the sign-in link:', describeErrorForLog(error));
      return LinkResendOutcome.SendFailed;
    } finally {
      await loading.dismiss();
    }
  }

  /** Says the link could not be sent and asks whether to try again. */
  private async askToTryAgainAsync(): Promise<boolean> {
    return await this.alertCtrl.showAlertAsync<boolean>({
      title: LL.auth.accountHint.title(),
      message: LL.auth.newAccountMade.couldNotSend(),
      buttons: [
        { text: LL.common.buttons.cancel(), role: AlertButtonRole.Cancel, result: false },
        { text: LL.common.buttons.tryAgain(), role: AlertButtonRole.Primary, result: true },
      ],
      implicitCancelResult: false,
    });
  }

  /** A one-button dialog under the same title, for each final outcome. */
  private async showMessageAsync(message: TranslatedString) {
    await this.alertCtrl.showAlertAsync({
      title: LL.auth.accountHint.title(),
      message,
      buttons: [{ text: LL.common.buttons.ok(), role: AlertButtonRole.Primary, result: null }],
      implicitCancelResult: null,
    });
  }

  /** How often the hint is checked, and what the check found. Never carries the address. */
  private trackChecked(trigger: AccountHintTrigger, accountHint: string, signedInEmail: string | null) {
    this.telemetry.trackEvent('account_hint_checked', {
      trigger,
      hint_comparison: compareWithHint(accountHint, signedInEmail),
    });
  }
}
